import {useEffect, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import type {TFunction} from 'i18next'
import {Box, Button, Typography} from '@mui/material'
import {useAppTokens, type ThemeTokens} from '../theme'
import WindowControls from '../components/WindowControls'
import FreeEditorPage from '../components/FreeEditorPage'
import {EditorConfigProvider} from '../components/EditorConfigContext'
import {trackEvent} from '../../common/analytics/trackEvent'

const PRANK_FADE_MS = 350

const NOISE_SVG =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>"

const LandingPage = () => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const tokens = useAppTokens()
  const sx = useMemo(() => buildStyles(tokens), [tokens])

  const [pranked, setPranked] = useState(false)
  const [editorMounted, setEditorMounted] = useState(true)

  const handleBrowseTemplates = () => {
    trackEvent('landing_hero_cta_click', {button: 'browse_projects'})
    navigate('/projects')
  }
  const handleTryFree = () => {
    trackEvent('landing_hero_cta_click', {button: 'try_free'})
    navigate('/free')
  }
  const handleMaximize = () => navigate('/free')
  const handlePrank = () => {
    setPranked(true)
    trackEvent('landing_prank_overlay_shown')
  }
  const handleSignUp = () => {
    trackEvent('landing_prank_signup_click')
    navigate('/login')
  }

  // Unmount the embedded editor after the fade-out completes so the R3F canvas stops rendering.
  useEffect(() => {
    if (!pranked) return
    const id = window.setTimeout(() => setEditorMounted(false), PRANK_FADE_MS)
    return () => window.clearTimeout(id)
  }, [pranked])

  const windowHandlers = {
    onClose: handlePrank,
    onMinimize: handlePrank,
    onMaximize: handleMaximize,
  }

  return (
    <Box sx={sx.root}>
      <Box sx={sx.bgGrid} />
      <Box sx={sx.noise} />

      <Box component="header" sx={sx.hero}>
        <Typography component="h1" sx={sx.heroTitle}>
          {t('landing.hero.titleLine1')}
          <br />
          {t('landing.hero.titleLine2')}
          <br />
          <Box component="em" sx={sx.heroEmphasis}>
            {t('landing.hero.titleEmphasis')}
          </Box>
        </Typography>

        <Typography component="p" sx={sx.heroSub}>
          {t('landing.hero.subtitle')}
        </Typography>

        <Box sx={sx.heroCtas}>
          <Button onClick={handleBrowseTemplates} sx={sx.btnPrimary} disableRipple>
            {t('landing.hero.ctaBrowseTemplates')}
          </Button>
          <Button onClick={handleTryFree} sx={sx.btnGhost} disableRipple>
            {t('landing.hero.ctaTryFree')}
          </Button>
        </Box>
      </Box>

      <Box sx={sx.editorWrap}>
        <Box sx={sx.editorShell}>
          <Box sx={sx.editorTopbar}>
            <WindowControls position="left" {...windowHandlers} />
            <Box sx={sx.editorTitle}>
              <Box component="b" sx={sx.editorTitleBold}>
                {t('landing.preview.title')}
              </Box>
            </Box>
            <WindowControls position="right" {...windowHandlers} />
          </Box>
          <Box sx={sx.editorBody}>
            {editorMounted && (
              <Box sx={{...sx.editorFade, opacity: pranked ? 0 : 1}}>
                <EditorConfigProvider redirectOnError={false} fallback={<Box sx={sx.editorFallback} />}>
                  <FreeEditorPage embedded />
                </EditorConfigProvider>
              </Box>
            )}
            {pranked && <PrankOverlay sx={sx} t={t} onSignUp={handleSignUp} />}
          </Box>
        </Box>
      </Box>

      <Box sx={sx.bottomSpace} />
    </Box>
  )
}

interface PrankOverlayProps {
  sx: ReturnType<typeof buildStyles>
  t: TFunction
  onSignUp: () => void
}

const PrankOverlay = ({sx, t, onSignUp}: PrankOverlayProps) => (
  <Box sx={sx.prankOverlay}>
    <Box component="img" src="/rickroll.gif" alt="" sx={sx.prankGif} />
    <Typography component="h2" sx={sx.prankHeading}>
      {t('landing.prank.heading')}
    </Typography>
    <Typography component="p" sx={sx.prankSub}>
      {t('landing.prank.sub')}
    </Typography>
    <Button onClick={onSignUp} sx={sx.prankCta} disableRipple>
      {t('landing.prank.cta')}
    </Button>
  </Box>
)

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    position: 'relative' as const,
    width: '100%',
  },
  bgGrid: {
    position: 'fixed' as const,
    inset: 0,
    pointerEvents: 'none' as const,
    backgroundImage: `
      linear-gradient(to right, rgba(255, 255, 255, 0.025) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(255, 255, 255, 0.025) 1px, transparent 1px)
    `,
    backgroundSize: '64px 64px',
    maskImage: 'radial-gradient(ellipse 80% 70% at 50% 30%, #000 30%, transparent 80%)',
    WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 50% 30%, #000 30%, transparent 80%)',
    zIndex: 0,
  },
  noise: {
    position: 'fixed' as const,
    inset: 0,
    pointerEvents: 'none' as const,
    opacity: 0.035,
    mixBlendMode: 'overlay' as const,
    zIndex: 1,
    backgroundImage: `url("${NOISE_SVG}")`,
  },
  hero: {
    position: 'relative' as const,
    zIndex: 2,
    padding: {xs: '56px 20px 28px', md: '80px 32px 40px'},
    maxWidth: 1280,
    margin: '0 auto',
    textAlign: 'center' as const,
  },
  heroTitle: {
    fontWeight: 700,
    fontSize: 'clamp(36px, 6.5vw, 86px)',
    lineHeight: 1.1,
    letterSpacing: '-0.025em',
    margin: '0 auto 32px',
    paddingBottom: '0.25em',
    maxWidth: '16ch',
    color: tokens.text.primary,
  },
  heroEmphasis: {
    fontStyle: 'italic',
    fontWeight: 500,
    background: `linear-gradient(180deg, #E5DCFF 0%, ${tokens.accent.light} 100%)`,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
  },
  heroSub: {
    fontSize: 'clamp(15px, 1.2vw, 18px)',
    lineHeight: 1.55,
    color: tokens.text.secondary,
    maxWidth: '56ch',
    margin: '0 auto 32px',
    fontWeight: 400,
  },
  heroCtas: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    alignItems: 'stretch',
    marginBottom: '20px',
    flexDirection: {xs: 'column' as const, sm: 'row' as const},
    width: {xs: '100%', sm: 'auto'},
    maxWidth: {xs: 360, sm: 'none'},
    marginLeft: {xs: 'auto', sm: 0},
    marginRight: {xs: 'auto', sm: 0},
  },
  btnPrimary: {
    height: 48,
    paddingX: '22px',
    fontSize: 15,
    fontWeight: 500,
    letterSpacing: '-0.005em',
    borderRadius: '11px',
    textTransform: 'none' as const,
    color: '#fff',
    background: `linear-gradient(180deg, ${tokens.accent.light}, ${tokens.accent.primary})`,
    border: '1px solid rgba(255, 255, 255, 0.18)',
    boxShadow: `
      0 0 0 1px rgba(124, 92, 255, 0.4),
      0 8px 24px rgba(124, 92, 255, 0.35),
      inset 0 1px 0 rgba(255, 255, 255, 0.3)
    `,
    transition: 'transform 0.15s ease, box-shadow 0.2s',
    '&:hover': {
      background: `linear-gradient(180deg, ${tokens.accent.light}, ${tokens.accent.primary})`,
      transform: 'translateY(-1px)',
      boxShadow: `
        0 0 0 1px rgba(124, 92, 255, 0.5),
        0 12px 32px rgba(124, 92, 255, 0.5),
        inset 0 1px 0 rgba(255, 255, 255, 0.35)
      `,
    },
  },
  btnGhost: {
    height: 48,
    paddingX: '22px',
    fontSize: 15,
    fontWeight: 500,
    letterSpacing: '-0.005em',
    borderRadius: '11px',
    textTransform: 'none' as const,
    color: tokens.text.secondary,
    background: 'transparent',
    border: `1px solid ${tokens.accordion.borderStrong}`,
    transition: 'color 0.2s, border-color 0.2s, background 0.2s',
    '&:hover': {
      color: tokens.text.primary,
      borderColor: 'rgba(255, 255, 255, 0.25)',
      background: 'rgba(255, 255, 255, 0.03)',
    },
  },
  editorWrap: {
    position: 'relative' as const,
    zIndex: 2,
    maxWidth: 1280,
    margin: {xs: '40px auto 0', md: '56px auto 0'},
    padding: {xs: '0 12px', md: '0 32px'},
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: '-40px 32px',
      borderRadius: '30px',
      background: `radial-gradient(ellipse 60% 80% at 50% 50%, ${tokens.accent.glow}, transparent 70%)`,
      filter: 'blur(40px)',
      opacity: 0.5,
      zIndex: -1,
    },
  },
  editorShell: {
    position: 'relative' as const,
    borderRadius: '18px',
    overflow: 'hidden' as const,
    background: `linear-gradient(180deg, ${tokens.surface.panel} 0%, ${tokens.surface.panelDeep} 100%)`,
    border: `1px solid ${tokens.accordion.borderStrong}`,
    boxShadow: `
      0 0 0 1px rgba(255, 255, 255, 0.02),
      0 40px 80px -20px rgba(0, 0, 0, 0.6),
      0 0 120px -20px ${tokens.accent.glow}
    `,
  },
  editorTopbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 44,
    padding: '0 14px',
    borderBottom: `1px solid ${tokens.accordion.border}`,
    background: 'rgba(255, 255, 255, 0.015)',
  },
  editorTitle: {
    fontSize: 12.5,
    color: tokens.text.muted,
    fontWeight: 500,
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    flex: '1 1 auto',
    textAlign: 'center' as const,
  },
  editorTitleBold: {
    color: tokens.text.secondary,
    fontWeight: 600,
  },
  editorBody: {
    height: {xs: 540, md: 680},
    position: 'relative' as const,
    overflow: 'hidden' as const,
    background: tokens.surface.background,
  },
  editorFade: {
    position: 'absolute' as const,
    inset: 0,
    transition: `opacity ${PRANK_FADE_MS}ms ease`,
    pointerEvents: 'auto' as const,
  },
  editorFallback: {
    width: '100%',
    height: '100%',
    background: `
      radial-gradient(ellipse 70% 60% at 50% 50%, rgba(124, 92, 255, 0.08), transparent 70%),
      linear-gradient(180deg, rgba(255, 255, 255, 0.01), rgba(0, 0, 0, 0.2))
    `,
  },
  prankOverlay: {
    position: 'absolute' as const,
    inset: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '20px',
    padding: {xs: '24px 20px', md: '32px'},
    textAlign: 'center' as const,
    background: `
      radial-gradient(ellipse 70% 60% at 50% 50%, rgba(124, 92, 255, 0.10), transparent 70%),
      linear-gradient(180deg, rgba(255, 255, 255, 0.01), rgba(0, 0, 0, 0.25))
    `,
    animation: `prankFadeIn ${PRANK_FADE_MS}ms ease`,
    '@keyframes prankFadeIn': {
      from: {opacity: 0},
      to: {opacity: 1},
    },
  },
  prankGif: {
    width: {xs: '70%', sm: 320},
    maxWidth: 360,
    borderRadius: '14px',
    border: `1px solid ${tokens.accordion.borderStrong}`,
    boxShadow: '0 12px 36px rgba(0, 0, 0, 0.45)',
  },
  prankHeading: {
    fontSize: 'clamp(20px, 2.6vw, 30px)',
    fontWeight: 700,
    letterSpacing: '-0.015em',
    lineHeight: 1.2,
    color: tokens.text.primary,
    margin: 0,
    maxWidth: '20ch',
  },
  prankSub: {
    fontSize: 14,
    color: tokens.text.secondary,
    margin: 0,
    maxWidth: '36ch',
  },
  prankCta: {
    height: 48,
    paddingX: '22px',
    fontSize: 15,
    fontWeight: 500,
    letterSpacing: '-0.005em',
    borderRadius: '11px',
    textTransform: 'none' as const,
    color: '#fff',
    background: `linear-gradient(180deg, ${tokens.accent.light}, ${tokens.accent.primary})`,
    border: '1px solid rgba(255, 255, 255, 0.18)',
    boxShadow: `
      0 0 0 1px rgba(124, 92, 255, 0.4),
      0 8px 24px rgba(124, 92, 255, 0.35),
      inset 0 1px 0 rgba(255, 255, 255, 0.3)
    `,
    transition: 'transform 0.15s ease, box-shadow 0.2s',
    '&:hover': {
      background: `linear-gradient(180deg, ${tokens.accent.light}, ${tokens.accent.primary})`,
      transform: 'translateY(-1px)',
      boxShadow: `
        0 0 0 1px rgba(124, 92, 255, 0.5),
        0 12px 32px rgba(124, 92, 255, 0.5),
        inset 0 1px 0 rgba(255, 255, 255, 0.35)
      `,
    },
  },
  bottomSpace: {
    height: {xs: 60, md: 120},
  },
})

export default LandingPage
